import { useState, useRef, useEffect } from "react";
import { X, Printer, Download, ImageIcon, ZoomIn, ZoomOut, Maximize } from "lucide-react";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";
import OrderPrintTemplate from "@/components/sales/print/OrderPrintTemplate";
import QuotationPrintTemplate from "@/components/sales/print/QuotationPrintTemplate";
import OutsourcePOPrintTemplate from "@/components/sales/print/OutsourcePOPrintTemplate";
import OutsourceReconciliationPrintTemplate from "@/components/sales/print/OutsourceReconciliationPrintTemplate";
import OrderProductionPrintTemplate from "@/components/sales/print/OrderProductionPrintTemplate";
import DeliveryPrintTemplate from "@/components/logistics/print/DeliveryPrintTemplate";
import PurchaseOrderPrintTemplate from "@/components/supply/print/PurchaseOrderPrintTemplate";
import TransactionPrintTemplate from "@/components/finance/print/TransactionPrintTemplate";
import MoldPrintTemplate from "@/pages/tech/molds/print/MoldPrintTemplate";
import NCRPrintTemplate from "@/components/quality/print/NCRPrintTemplate";
import ProductionOrderPrintTemplate from "@/components/planning/print/ProductionOrderPrintTemplate";
import CostBreakdownPrintTemplate from "@/components/sales/print/CostBreakdownPrintTemplate";
import CustomerMoqPrintTemplate from "@/components/sales/print/CustomerMoqPrintTemplate";
import WarehouseDocumentPrintTemplate from "@/components/warehouse/print/WarehouseDocumentPrintTemplate";
import WarehouseInventoryPrintTemplate from "@/components/warehouse/print/WarehouseInventoryPrintTemplate";
import QCFinalPrintTemplate from "@/components/quality/print/QCFinalPrintTemplate";
import QCMaterialPrintTemplate from "@/components/quality/print/QCMaterialPrintTemplate";
import QCProcessPrintTemplate from "@/components/quality/print/QCProcessPrintTemplate";
import CoAPrintTemplate from "@/components/quality/print/CoAPrintTemplate";
import WipTransferPrintTemplate from "@/components/factory/print/WipTransferPrintTemplate";
import StocktakeBalancePrintTemplate from "@/components/warehouse/print/StocktakeBalancePrintTemplate";
import DebtReconciliationPrintTemplate from "@/components/finance/print/DebtReconciliationPrintTemplate";
import VendorDebtReconciliationPrintTemplate from "@/components/finance/print/VendorDebtReconciliationPrintTemplate";
import PlatePrintTemplate from "@/components/tech/print/PlatePrintTemplate";
import PlateHandoverPrintTemplate from "@/pages/tech/plates/print/PlateHandoverPrintTemplate";
import type { Order, Quotation } from "@/shared/schemas";

export interface PrintDialogProps {
    open?: boolean;
    isOpen?: boolean;
    onClose: () => void;
    type?: 'order' | 'quotation' | 'outsource' | 'outsource_reconciliation' | 'production' | 'delivery' | 'purchase_order' | 'receipt' | 'spec' | 'mold' | 'ncr' | 'wip_transfer' | 'material_inventory' | 'material_receipt' | 'material_issue' | 'fg_inventory' | 'fg_receipt' | 'fg_issue' | 'tooling_inventory' | 'tooling_receipt' | 'tooling_issue' | 'tech_inventory' | 'tech_receipt' | 'tech_issue' | 'lsx' | 'custom' | 'cost-breakdown-moq' | 'customer-moq-quotation' | 'qc_final' | 'qc_material' | 'qc_process' | 'coa' | 'stocktake_balance' | 'debt_reconciliation' | 'vendor_debt_reconciliation' | 'ctp_plate' | 'plate_handover' | 'warehouse_doc';
    data?: any | any[];
    orientation?: 'portrait' | 'landscape';
    children?: React.ReactNode;
    title?: string;
    documentTitle?: string;
    extraControls?: React.ReactNode;
}

/**
 * Full-screen lightbox overlay for print preview.
 * Opens inline — no new tab. Pressing ESC or clicking X returns to the view.
 */
export default function PrintDialog({ open, isOpen, onClose, type, data, orientation, children, title, documentTitle, extraControls }: PrintDialogProps) {
    const isActualOpen = open ?? isOpen ?? false;
    const [showImages, setShowImages] = useState(true);
    const [isBlankForm, setIsBlankForm] = useState(false);
    const [hidePrices, setHidePrices] = useState(false);
    const printRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const companyConfig = useCompanyConfig(isActualOpen);
    const [downloading, setDownloading] = useState(false);

    // Zoom state
    const initialScale = typeof window !== 'undefined' && window.innerWidth < 800 ? window.innerWidth / 850 : 1;
    const [scale, setScale] = useState(initialScale);

    useEffect(() => {
        if (!isActualOpen) return;
        const container = containerRef.current;
        if (!container) return;
        
        const handleWheel = (e: WheelEvent) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault(); // Stop native zoom/scroll
                setScale(s => {
                    const zoomSensitivity = 0.002;
                    let newScale = s - e.deltaY * zoomSensitivity;
                    return Math.min(Math.max(newScale, 0.2), 4);
                });
            }
        };

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel);
    }, [isActualOpen]);

    const zoomIn = () => setScale(s => Math.min(s + 0.2, 4));
    const zoomOut = () => setScale(s => Math.max(s - 0.2, 0.2));
    const resetTransform = () => setScale(initialScale);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isActualOpen) {
            window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isActualOpen, onClose]);

    if (!isActualOpen) return null;
    if (!data && type !== 'custom' && !children) return null;

    const handlePrint = () => {
        const printArea = printRef.current;
        if (!printArea) return;

        const styleSheets = Array.from(document.styleSheets);
        let cssText = '';
        for (const sheet of styleSheets) {
            try {
                if (sheet.href) {
                    cssText += `<link rel="stylesheet" href="${sheet.href}" />\n`;
                } else if (sheet.cssRules) {
                    const rules = Array.from(sheet.cssRules).map(r => r.cssText).join('\n');
                    cssText += `<style>${rules}</style>\n`;
                }
            } catch {
                if (sheet.href) cssText += `<link rel="stylesheet" href="${sheet.href}" />\n`;
            }
        }

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);

        const iframeDoc = iframe.contentWindow?.document;
        if (!iframeDoc) {
            window.print();
            document.body.removeChild(iframe);
            return;
        }

        iframeDoc.write(`
            <!DOCTYPE html>
            <html><head>
            <title>${documentTitle || 'Print'}</title>
            <style>
                @page { size: A4 ${orientation || (type === 'production' || type === 'cost-breakdown-moq' ? 'landscape' : 'portrait')}; margin: 10mm; }
                body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; font-family: 'Times New Roman', Times, serif; }
                * { box-sizing: border-box; }
                #print-area { 
                    max-width: none !important; 
                    width: 100% !important;
                    min-height: auto !important; 
                    box-shadow: none !important;
                    margin: 0 !important;
                }
                /* Enforce solid black borders for all tables and cells during print inside the iframe */
                #print-area table, #print-area th, #print-area td, #print-area *, table, th, td {
                    border-color: #000000 !important;
                }
                @media print {
                    .break-inside-avoid, tr, tfoot, .grid {
                        page-break-inside: avoid !important;
                        break-inside: avoid !important;
                    }
                    .page-break-after-always {
                        page-break-after: always !important;
                        break-after: page !important;
                    }
                    /* Compact margins slightly during print to fit page limits */
                    .mb-4, .my-4 { margin-bottom: 0.5rem !important; }
                    .mt-4, .my-4 { margin-top: 0.5rem !important; }
                    .mb-5 { margin-bottom: 0.5rem !important; }
                    .mt-6 { margin-top: 0.75rem !important; }
                    .mb-8 { margin-bottom: 0.75rem !important; }
                    .mb-6 { margin-bottom: 0.75rem !important; }
                    .mt-10 { margin-top: 1rem !important; }
                    .pb-3 { padding-bottom: 0.5rem !important; }
                }
            </style>
            ${cssText}
            </head><body>
            ${printArea.innerHTML}
            </body></html>
        `);
        iframeDoc.close();

        // Need to wait for styles to load
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 500);
    };

    const isLandscape = orientation === 'landscape' || type === 'production' || type === 'cost-breakdown-moq';

    const handleDownloadPDF = async () => {
        const printArea = printRef.current;
        if (!printArea) return;

        setDownloading(true);
        try {
            const html2canvasMod = await import("html2canvas-pro");
            const html2canvas = html2canvasMod.default || html2canvasMod;
            const { jsPDF } = await import("jspdf");

            // Disable container box shadow temporarily to avoid capture artifacts
            const originalShadow = printArea.style.boxShadow;
            const originalTransform = printArea.style.transform;
            
            printArea.style.boxShadow = "none";
            printArea.style.transform = "none";

            const canvas = await html2canvas(printArea, {
                scale: 2, // 2x scale for high quality
                useCORS: true,
                allowTaint: false,
                backgroundColor: "#ffffff",
                logging: false,
                windowWidth: isLandscape ? 1131 : 800, // Native element width
                windowHeight: isLandscape ? 800 : 1131,
            });

            // Restore style properties
            printArea.style.boxShadow = originalShadow;
            printArea.style.transform = originalTransform;

            const imgData = canvas.toDataURL("image/jpeg", 0.95);
            const pdfWidth = isLandscape ? 297 : 210;
            const pdfHeight = isLandscape ? 210 : 297;
            const margin = 10; // 10mm margin for Y axis cut lines
            const usableHeight = pdfHeight - margin * 2;
            
            const pdf = new jsPDF({
                orientation: isLandscape ? "landscape" : "portrait",
                unit: "mm",
                format: "a4",
            });

            // Use the full pdfWidth because the DOM element already has 10mm padding on X axis
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * pdfWidth) / canvas.width;
            
            const numPages = Math.ceil(imgHeight / pdfHeight);
            
            let finalImgWidth = imgWidth;
            let finalImgHeight = imgHeight;
            
            // If the overflow into the last page is very small, scale down
            const overflow = imgHeight % pdfHeight;
            if (numPages > 1 && overflow > 0 && overflow < pdfHeight * 0.08) {
                const targetHeight = (numPages - 1) * pdfHeight;
                const ratio = targetHeight / imgHeight;
                finalImgWidth = imgWidth * ratio;
                finalImgHeight = targetHeight;
            }

            let position = 0;
            pdf.addImage(imgData, "JPEG", 0, position, finalImgWidth, finalImgHeight);
            
            // Draw white masks to enforce Y margins
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pdfWidth, margin, "F"); // Top mask
            pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F"); // Bottom mask
            
            // Page 1 shows content from 0 to (pdfHeight - margin)
            let heightLeft = finalImgHeight - (pdfHeight - margin);

            // Add subsequent pages if there's content left
            while (heightLeft > 0) {
                position -= usableHeight;
                pdf.addPage();
                pdf.addImage(imgData, "JPEG", 0, position, finalImgWidth, finalImgHeight);
                
                // Draw white masks to enforce Y margins
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, pdfWidth, margin, "F"); // Top mask
                pdf.rect(0, pdfHeight - margin, pdfWidth, margin, "F"); // Bottom mask
                
                heightLeft -= usableHeight;
            }

            const docName = `${type}-${data?.orderId || data?.quotationId || data?.id || documentTitle || "document"}.pdf`;
            pdf.save(docName);
        } catch (err) {
            console.error("Direct PDF generation failed:", err);
            // Fallback to browser print
            handlePrint();
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex flex-col bg-black/80 backdrop-blur-sm cursor-pointer" onClick={onClose}>
                        {/* Toolbar */}
                        <div
                            className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm relative z-50"
                            onClick={e => e.stopPropagation()}
                        >
                            <div className={`mx-auto flex items-center justify-between px-4 sm:px-6 py-3 ${isLandscape ? 'max-w-[1131px]' : 'max-w-[800px]'}`}>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={onClose}
                                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                                    >
                                        <X className="h-4 w-4" />
                                        Đóng
                                    </button>
                                    {extraControls}
                                </div>
                                <div className="flex items-center gap-2 sm:gap-3">
                                    <div className="hidden sm:flex items-center bg-slate-100 rounded-lg p-1 mr-2 border border-slate-200">
                                        <button onClick={() => zoomOut()} className="p-1.5 hover:bg-white rounded text-slate-500 hover:text-slate-900 transition-colors shadow-sm cursor-pointer" title="Thu nhỏ">
                                            <ZoomOut className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => resetTransform()} className="p-1.5 hover:bg-white rounded text-slate-500 hover:text-slate-900 transition-colors shadow-sm cursor-pointer mx-0.5" title="Mặc định">
                                            <Maximize className="h-4 w-4" />
                                        </button>
                                        <button onClick={() => zoomIn()} className="p-1.5 hover:bg-white rounded text-slate-500 hover:text-slate-900 transition-colors shadow-sm cursor-pointer" title="Phóng to">
                                            <ZoomIn className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer select-none px-2 sm:px-0">
                                        <input
                                            type="checkbox"
                                            checked={showImages}
                                            onChange={e => setShowImages(e.target.checked)}
                                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4 cursor-pointer"
                                        />
                                        <span className="flex items-center gap-1 text-sm text-slate-600 whitespace-nowrap">
                                            <ImageIcon className="h-3.5 w-3.5 hidden sm:inline-block" /> <span className="hidden sm:inline-block">Hình ảnh</span>
                                        </span>
                                    </label>
                                    {['material_issue', 'fg_issue', 'tooling_issue', 'tech_issue', 'warehouse_doc', 'wip_transfer', 'production'].includes(type as string) && (
                                        <label className="flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 bg-slate-100 text-slate-800 border border-slate-300 rounded text-xs font-semibold whitespace-nowrap hover:bg-slate-200 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={hidePrices}
                                                onChange={e => setHidePrices(e.target.checked)}
                                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
                                            />
                                            <span>🔒 Ẩn giá</span>
                                        </label>
                                    )}
                                    {type === 'lsx' && (
                                        <label className="flex items-center gap-1.5 cursor-pointer select-none px-2 py-1 bg-amber-50 text-amber-900 border border-amber-300 rounded text-xs font-semibold whitespace-nowrap hover:bg-amber-100 transition-colors">
                                            <input
                                                type="checkbox"
                                                checked={isBlankForm}
                                                onChange={e => setIsBlankForm(e.target.checked)}
                                                className="rounded border-amber-300 text-amber-600 focus:ring-amber-500 h-3.5 w-3.5 cursor-pointer"
                                            />
                                            <span>📝 Form trắng A4</span>
                                        </label>
                                    )}
                                    <button
                                        onClick={handleDownloadPDF}
                                        disabled={downloading}
                                        className="flex items-center gap-1.5 sm:gap-2 bg-emerald-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm font-medium shadow-sm whitespace-nowrap cursor-pointer disabled:opacity-50"
                                    >
                                        {downloading ? (
                                            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        <span className="hidden sm:inline-block">
                                            {downloading ? "Đang tải..." : "Tải PDF"}
                                        </span>
                                        <span className="sm:hidden">{downloading ? "..." : "PDF"}</span>
                                    </button>
                                    <button
                                        onClick={handlePrint}
                                        className="flex items-center gap-1.5 sm:gap-2 bg-indigo-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-indigo-700 text-sm font-medium shadow-sm whitespace-nowrap cursor-pointer"
                                    >
                                        <Printer className="h-4 w-4" />
                                        <span className="hidden sm:inline-block">In Ngay</span>
                                        <span className="sm:hidden">In</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable preview area */}
                        <div 
                            ref={containerRef}
                            className="flex-1 bg-transparent w-full relative overflow-auto"
                        >
                            <div className="p-4 md:p-12 flex items-start justify-center min-h-full">
                                <div style={{ transform: `scale(${scale})`, transformOrigin: 'top center', transition: 'transform 0.1s ease-out' }}>
                                    <div 
                                        className={`bg-white shadow-2xl print-a4-container cursor-default flex-shrink-0 ${
                                            isLandscape 
                                                 ? 'w-[1131px] min-h-[800px] is-landscape' 
                                                 : 'w-[800px] min-h-[1131px]'
                                        }`}
                                        ref={printRef}
                                        onClick={e => e.stopPropagation()}
                                        style={{ marginBottom: '40px' }}
                                    >
                                    {(() => {
                                        if (type === 'custom' || children) {
                                            return <>{children}</>;
                                        }
                                        const dataArray = Array.isArray(data) ? data : [data];
                                        
                                        if (['material_inventory', 'fg_inventory', 'tooling_inventory', 'tech_inventory'].includes(type as string)) {
                                            return (
                                                <WarehouseInventoryPrintTemplate data={dataArray} config={companyConfig} typeLabel={
                                                    type === 'material_inventory' ? "BÁO CÁO TỒN KHO VẬT TƯ" :
                                                    type === 'fg_inventory' ? "BÁO CÁO TỒN KHO THÀNH PHẨM" :
                                                    type === 'tooling_inventory' ? "BÁO CÁO TỒN KHO CÔNG CỤ DỤNG CỤ" :
                                                    "BÁO CÁO TỒN KHO KỸ THUẬT"
                                                } />
                                            );
                                        }

                                        return dataArray.map((item: any, index: number) => (
                                            <div key={index} className={index < dataArray.length - 1 ? "page-break-after-always" : ""}>
                                                {type === 'order' ? (
                                                    <OrderPrintTemplate order={item as Order} config={companyConfig} showImages={showImages} />
                                                ) : type === 'quotation' ? (
                                                    <QuotationPrintTemplate quotation={item as Quotation} config={companyConfig} showImages={showImages} />
                                                ) : type === 'production' ? (
                                                    <OrderProductionPrintTemplate order={item as Order} config={companyConfig} showImages={showImages} />
                                                ) : type === 'delivery' ? (
                                                    <DeliveryPrintTemplate delivery={item} config={companyConfig} />
                                                ) : type === 'purchase_order' ? (
                                                    <PurchaseOrderPrintTemplate po={item} config={companyConfig} />
                                                ) : type === 'receipt' ? (
                                                    <TransactionPrintTemplate receipt={item} config={companyConfig} />
                                                ) : type === 'mold' ? (
                                                    <MoldPrintTemplate mold={item} data={item} config={companyConfig} />
                                                ) : type === 'ncr' ? (
                                                    <NCRPrintTemplate ncr={item} config={companyConfig} />
                                                ) : type === 'qc_final' ? (
                                                    <QCFinalPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'coa' ? (
                                                    <CoAPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'qc_material' ? (
                                                    <QCMaterialPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'qc_process' ? (
                                                    <QCProcessPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'wip_transfer' ? (
                                                    <WipTransferPrintTemplate transfer={item} config={companyConfig} />
                                                ) : type === 'stocktake_balance' ? (
                                                    <StocktakeBalancePrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'debt_reconciliation' ? (
                                                    <DebtReconciliationPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'vendor_debt_reconciliation' ? (
                                                    <VendorDebtReconciliationPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'ctp_plate' ? (
                                                    <PlatePrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'plate_handover' ? (
                                                    <PlateHandoverPrintTemplate data={item} plate={item} config={companyConfig} />
                                                ) : type === 'cost-breakdown-moq' ? (
                                                    <CostBreakdownPrintTemplate data={item} config={companyConfig} />
                                                ) : type === 'customer-moq-quotation' ? (
                                                    <CustomerMoqPrintTemplate data={item} config={companyConfig} />
                                                ) : ['material_receipt', 'fg_receipt', 'tooling_receipt', 'material_issue', 'fg_issue', 'tooling_issue', 'tech_receipt', 'tech_issue', 'warehouse_doc'].includes(type as string) ? (
                                                    <WarehouseDocumentPrintTemplate document={item} config={companyConfig} hidePrices={hidePrices} typeLabel={
                                                        type === 'warehouse_doc' ? (item.type === 'RECEIPT' ? "PHIẾU NHẬP KHO" : item.type === 'TRANSFER' ? "PHIẾU CHUYỂN KHO" : "PHIẾU XUẤT KHO") :
                                                        type?.includes('receipt') ? "PHIẾU NHẬP KHO" : "PHIẾU XUẤT KHO"
                                                    } />
                                                ) : type === 'lsx' ? (
                                                    <ProductionOrderPrintTemplate 
                                                        lsx={item.lsx} 
                                                        salesOrder={item.salesOrder} 
                                                        orderProducts={item.orderProducts} 
                                                        config={companyConfig}
                                                        showImages={showImages}
                                                        isBlankForm={isBlankForm}
                                                    />
                                                ) : type === 'outsource_reconciliation' ? (
                                                    <OutsourceReconciliationPrintTemplate po={item} config={companyConfig} />
                                                ) : (
                                                    <OutsourcePOPrintTemplate po={item} config={companyConfig} />
                                                )}
                                            </div>
                                        ));
                                    })()}
                                    </div>
                                </div>
                            </div>
                        </div>
        </div>
    );
}
